'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import * as XLSX from 'xlsx';

interface DORAMetrics {
  deploymentFrequency: string; // per day/week/month
  deploymentFrequencyValue: number;
  leadTime: number; // hours
  changeFailureRate: number; // percentage
  mttr: number; // hours
}

const DORA_BENCHMARKS = {
  elite: {
    deploymentFrequency: 'Multiple per day',
    deploymentFrequencyValue: 10,
    leadTime: 1, // < 1 hour
    changeFailureRate: 5, // 0-15%
    mttr: 1, // < 1 hour
  },
  high: {
    deploymentFrequency: 'Weekly to monthly',
    deploymentFrequencyValue: 4,
    leadTime: 24, // < 1 day
    changeFailureRate: 10, // 0-15%
    mttr: 24, // < 1 day
  },
  medium: {
    deploymentFrequency: 'Monthly to bimonthly',
    deploymentFrequencyValue: 1,
    leadTime: 168, // 1 week - 1 month
    changeFailureRate: 30, // 16-30%
    mttr: 168, // 1 week - 1 month
  },
  low: {
    deploymentFrequency: 'Less than monthly',
    deploymentFrequencyValue: 0.25,
    leadTime: 720, // > 1 month
    changeFailureRate: 50, // > 30%
    mttr: 720, // > 1 month
  },
};

export default function EngineeringMetricsDashboard() {
  const [metrics, setMetrics] = useState<DORAMetrics>({
    deploymentFrequency: 'Weekly to monthly',
    deploymentFrequencyValue: 4,
    leadTime: 48,
    changeFailureRate: 12,
    mttr: 6,
  });

  const [customMetrics, setCustomMetrics] = useState([
    { name: 'Code Review Time', value: '', unit: 'hours', target: '24' },
  ]);

  const doraLevel = useMemo(() => {
    let score = 0;

    // Deployment Frequency
    if (metrics.deploymentFrequencyValue >= 10) score += 4;
    else if (metrics.deploymentFrequencyValue >= 4) score += 3;
    else if (metrics.deploymentFrequencyValue >= 1) score += 2;
    else score += 1;

    // Lead Time
    if (metrics.leadTime <= 1) score += 4;
    else if (metrics.leadTime <= 24) score += 3;
    else if (metrics.leadTime <= 168) score += 2;
    else score += 1;

    // Change Failure Rate
    if (metrics.changeFailureRate <= 15) score += 4;
    else if (metrics.changeFailureRate <= 30) score += 2;
    else score += 1;

    // MTTR
    if (metrics.mttr <= 1) score += 4;
    else if (metrics.mttr <= 24) score += 3;
    else if (metrics.mttr <= 168) score += 2;
    else score += 1;

    const avg = score / 4;
    if (avg >= 3.5) return 'Elite';
    if (avg >= 2.5) return 'High';
    if (avg >= 1.5) return 'Medium';
    return 'Low';
  }, [metrics]);

  const addCustomMetric = () => {
    setCustomMetrics([
      ...customMetrics,
      { name: '', value: '', unit: 'hours', target: '' },
    ]);
  };

  const removeCustomMetric = (index: number) => {
    setCustomMetrics(customMetrics.filter((_, i) => i !== index));
  };

  const updateCustomMetric = (
    index: number,
    field: 'name' | 'value' | 'unit' | 'target',
    value: string
  ) => {
    const updated = [...customMetrics];
    updated[index][field] = value;
    setCustomMetrics(updated);
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    if (!wb.Workbook) wb.Workbook = {};
    (wb.Workbook as any).CalcPr = { fullCalcOnLoad: true };

    // DORA Metrics sheet
    const doraData = [
      ['Engineering Metrics Dashboard'],
      ['Generated on:', new Date().toLocaleDateString()],
      [],
      ['DORA Metrics'],
      ['Metric', 'Your Value', 'Performance Level'],
      ['Deployment Frequency', metrics.deploymentFrequency, doraLevel],
      ['Lead Time for Changes', `${metrics.leadTime} hours`, doraLevel],
      ['Change Failure Rate', `${metrics.changeFailureRate}%`, doraLevel],
      ['Mean Time to Recovery (MTTR)', `${metrics.mttr} hours`, doraLevel],
      [],
      ['Overall DORA Level:', doraLevel],
      [],
      ['Benchmarks'],
      ['Level', 'Deploy Frequency', 'Lead Time', 'Change Failure', 'MTTR'],
      [
        'Elite',
        DORA_BENCHMARKS.elite.deploymentFrequency,
        '< 1 hour',
        '0-15%',
        '< 1 hour',
      ],
      ['High', DORA_BENCHMARKS.high.deploymentFrequency, '< 1 day', '0-15%', '< 1 day'],
      [
        'Medium',
        DORA_BENCHMARKS.medium.deploymentFrequency,
        '1 week - 1 month',
        '16-30%',
        '1 week - 1 month',
      ],
      ['Low', DORA_BENCHMARKS.low.deploymentFrequency, '> 1 month', '> 30%', '> 1 month'],
    ];

    const doraSheet = XLSX.utils.aoa_to_sheet(doraData);
    XLSX.utils.book_append_sheet(wb, doraSheet, 'DORA Metrics');

    // Custom Metrics sheet
    const customData = [
      ['Custom Metrics'],
      [],
      ['Metric Name', 'Current Value', 'Unit', 'Target', 'Status'],
      ...customMetrics
        .filter((m) => m.name && m.value)
        .map((m) => {
          const current = parseFloat(m.value) || 0;
          const target = parseFloat(m.target) || 0;
          const status = target > 0 ? (current <= target ? 'On Track' : 'Needs Improvement') : '-';
          return [m.name, m.value, m.unit, m.target || '-', status];
        }),
    ];

    const customSheet = XLSX.utils.aoa_to_sheet(customData);
    XLSX.utils.book_append_sheet(wb, customSheet, 'Custom Metrics');

    XLSX.writeFile(wb, `engineering-metrics-${Date.now()}.xlsx`);
  };

  return (
    <div className="container mx-auto px-4 py-12 max-w-6xl">
      <div className="mb-8">
        <Link href="/tools" className="text-accent hover:text-accent transition-colors">
          ← Back to Tools
        </Link>
      </div>

      <div className="mb-12">
        <h1 className="text-4xl font-bold text-light-text dark:text-foreground mb-4">
          Engineering Metrics Dashboard
        </h1>
        <p className="text-xl text-light-text-secondary dark:text-foreground-secondary">
          Track DORA metrics and custom KPIs to measure engineering team performance
        </p>
      </div>

      {/* DORA Level Badge */}
      <div className="mb-8 text-center">
        <div
          className={`inline-block px-8 py-4 rounded-lg border-2 ${
            doraLevel === 'Elite'
              ? 'bg-green-500/20 border-green-500'
              : doraLevel === 'High'
              ? 'bg-blue-500/20 border-blue-500'
              : doraLevel === 'Medium'
              ? 'bg-yellow-500/20 border-yellow-500'
              : 'bg-red-500/20 border-red-500'
          }`}
        >
          <div className="text-sm text-light-text-secondary dark:text-foreground-secondary mb-1">
            DORA Performance Level
          </div>
          <div
            className={`text-3xl font-bold ${
              doraLevel === 'Elite'
                ? 'text-green-500'
                : doraLevel === 'High'
                ? 'text-blue-500'
                : doraLevel === 'Medium'
                ? 'text-yellow-500'
                : 'text-red-500'
            }`}
          >
            {doraLevel}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Input Form */}
        <div className="space-y-6">
          {/* DORA Metrics */}
          <div className="bg-white dark:bg-surface p-6 rounded-lg border border-gray-200 dark:border-border">
            <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6">
              DORA Metrics
            </h2>

            <div className="space-y-4">
              {/* Deployment Frequency */}
              <div>
                <label className="block text-sm font-semibold text-light-text dark:text-foreground mb-2">
                  Deployment Frequency
                </label>
                <select
                  value={metrics.deploymentFrequency}
                  onChange={(e) => {
                    const val = e.target.value;
                    let numVal = 1;
                    if (val.includes('Multiple per day')) numVal = 10;
                    else if (val.includes('Weekly')) numVal = 4;
                    else if (val.includes('Monthly')) numVal = 1;
                    else numVal = 0.25;

                    setMetrics({
                      ...metrics,
                      deploymentFrequency: val,
                      deploymentFrequencyValue: numVal,
                    });
                  }}
                  className="w-full px-4 py-2 bg-white dark:bg-surfaceer border border-gray-300 dark:border-border rounded text-light-text dark:text-foreground"
                >
                  <option value="Multiple per day">Multiple deployments per day</option>
                  <option value="Daily">Daily deployments</option>
                  <option value="Weekly to monthly">Weekly to monthly</option>
                  <option value="Monthly to bimonthly">Monthly to bimonthly</option>
                  <option value="Less than monthly">Less than monthly</option>
                </select>
              </div>

              {/* Lead Time */}
              <div>
                <label className="block text-sm font-semibold text-light-text dark:text-foreground mb-2">
                  Lead Time for Changes (hours)
                </label>
                <input
                  type="number" inputMode="numeric"
                  min="0"
                  value={metrics.leadTime}
                  onChange={(e) =>
                    setMetrics({ ...metrics, leadTime: parseFloat(e.target.value) || 0 })
                  }
                  className="w-full px-4 py-2 bg-white dark:bg-surfaceer border border-gray-300 dark:border-border rounded text-light-text dark:text-foreground"
                  placeholder="e.g., 24"
                />
                <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                  Time from code commit to production deployment
                </p>
              </div>

              {/* Change Failure Rate */}
              <div>
                <label className="block text-sm font-semibold text-light-text dark:text-foreground mb-2">
                  Change Failure Rate (%)
                </label>
                <input
                  type="number" inputMode="numeric"
                  min="0"
                  max="100"
                  value={metrics.changeFailureRate}
                  onChange={(e) =>
                    setMetrics({ ...metrics, changeFailureRate: parseFloat(e.target.value) || 0 })
                  }
                  className="w-full px-4 py-2 bg-white dark:bg-surfaceer border border-gray-300 dark:border-border rounded text-light-text dark:text-foreground"
                  placeholder="e.g., 12"
                />
                <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                  Percentage of deployments that fail in production
                </p>
              </div>

              {/* MTTR */}
              <div>
                <label className="block text-sm font-semibold text-light-text dark:text-foreground mb-2">
                  Mean Time to Recovery (hours)
                </label>
                <input
                  type="number" inputMode="numeric"
                  min="0"
                  value={metrics.mttr}
                  onChange={(e) =>
                    setMetrics({ ...metrics, mttr: parseFloat(e.target.value) || 0 })
                  }
                  className="w-full px-4 py-2 bg-white dark:bg-surfaceer border border-gray-300 dark:border-border rounded text-light-text dark:text-foreground"
                  placeholder="e.g., 6"
                />
                <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                  Time to recover from production failures
                </p>
              </div>
            </div>
          </div>

          {/* Custom Metrics */}
          <div className="bg-white dark:bg-surface p-6 rounded-lg border border-gray-200 dark:border-border">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-light-text dark:text-foreground">
                Custom Metrics
              </h2>
              <button
                onClick={addCustomMetric}
                className="bg-accent text-accent-foreground px-4 py-2 rounded font-semibold hover:bg-accent-hover transition-colors text-sm"
              >
                + Add Metric
              </button>
            </div>

            <div className="space-y-4">
              {customMetrics.map((metric, index) => (
                <div
                  key={index}
                  className="p-4 bg-gray-50 dark:bg-surfaceer rounded border border-gray-200 dark:border-border-muted"
                >
                  <div className="flex justify-between items-start mb-3">
                    <input
                      type="text"
                      placeholder="Metric name (e.g., Code Review Time)"
                      value={metric.name}
                      onChange={(e) => updateCustomMetric(index, 'name', e.target.value)}
                      className="flex-1 px-3 py-2 bg-white dark:bg-background border border-gray-300 dark:border-border rounded text-sm text-light-text dark:text-foreground"
                    />
                    <button
                      onClick={() => removeCustomMetric(index)}
                      className="ml-2 text-red-500 hover:text-red-600 text-sm"
                    >
                      Remove
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="number" inputMode="numeric"
                      placeholder="Value"
                      value={metric.value}
                      onChange={(e) => updateCustomMetric(index, 'value', e.target.value)}
                      className="px-3 py-2 bg-white dark:bg-background border border-gray-300 dark:border-border rounded text-sm text-light-text dark:text-foreground"
                    />
                    <select
                      value={metric.unit}
                      onChange={(e) => updateCustomMetric(index, 'unit', e.target.value)}
                      className="px-3 py-2 bg-white dark:bg-background border border-gray-300 dark:border-border rounded text-sm text-light-text dark:text-foreground"
                    >
                      <option value="hours">hours</option>
                      <option value="days">days</option>
                      <option value="%">%</option>
                      <option value="count">count</option>
                    </select>
                    <input
                      type="number" inputMode="numeric"
                      placeholder="Target"
                      value={metric.target}
                      onChange={(e) => updateCustomMetric(index, 'target', e.target.value)}
                      className="px-3 py-2 bg-white dark:bg-background border border-gray-300 dark:border-border rounded text-sm text-light-text dark:text-foreground"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Premium Features */}
          <div className="bg-accent-muted border border-border rounded-lg p-6">
            <h3 className="text-lg font-bold text-accent mb-3">🔒 Pro Features</h3>
            <ul className="space-y-2 text-sm text-light-text-secondary dark:text-foreground-secondary">
              <li>• Custom KPI builder</li>
              <li>• Historical trend analysis (6 months)</li>
              <li>• Multiple team tracking</li>
              <li>• Industry benchmark database</li>
              <li>• Real-time GitHub/GitLab integration</li>
            </ul>
            <Link
              href="/pricing"
              className="mt-4 block text-center bg-accent text-accent-foreground px-4 py-2 rounded font-semibold hover:bg-accent-hover transition-colors"
            >
              Upgrade to Pro
            </Link>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-6">
          {/* DORA Breakdown */}
          <div className="bg-white dark:bg-surface p-6 rounded-lg border border-gray-200 dark:border-border">
            <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-4">
              DORA Breakdown
            </h2>
            <div className="space-y-4">
              {/* Deployment Frequency */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-light-text dark:text-foreground">
                    Deployment Frequency
                  </span>
                  <span
                    className={`text-xs font-bold ${
                      metrics.deploymentFrequencyValue >= 10
                        ? 'text-green-500'
                        : metrics.deploymentFrequencyValue >= 4
                        ? 'text-blue-500'
                        : metrics.deploymentFrequencyValue >= 1
                        ? 'text-yellow-500'
                        : 'text-red-500'
                    }`}
                  >
                    {metrics.deploymentFrequencyValue >= 10
                      ? 'Elite'
                      : metrics.deploymentFrequencyValue >= 4
                      ? 'High'
                      : metrics.deploymentFrequencyValue >= 1
                      ? 'Medium'
                      : 'Low'}
                  </span>
                </div>
                <div className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                  {metrics.deploymentFrequency}
                </div>
              </div>

              {/* Lead Time */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-light-text dark:text-foreground">
                    Lead Time for Changes
                  </span>
                  <span
                    className={`text-xs font-bold ${
                      metrics.leadTime <= 1
                        ? 'text-green-500'
                        : metrics.leadTime <= 24
                        ? 'text-blue-500'
                        : metrics.leadTime <= 168
                        ? 'text-yellow-500'
                        : 'text-red-500'
                    }`}
                  >
                    {metrics.leadTime <= 1
                      ? 'Elite'
                      : metrics.leadTime <= 24
                      ? 'High'
                      : metrics.leadTime <= 168
                      ? 'Medium'
                      : 'Low'}
                  </span>
                </div>
                <div className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                  {metrics.leadTime} hours
                </div>
              </div>

              {/* Change Failure Rate */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-light-text dark:text-foreground">
                    Change Failure Rate
                  </span>
                  <span
                    className={`text-xs font-bold ${
                      metrics.changeFailureRate <= 15
                        ? 'text-green-500'
                        : metrics.changeFailureRate <= 30
                        ? 'text-yellow-500'
                        : 'text-red-500'
                    }`}
                  >
                    {metrics.changeFailureRate <= 15
                      ? 'Elite/High'
                      : metrics.changeFailureRate <= 30
                      ? 'Medium'
                      : 'Low'}
                  </span>
                </div>
                <div className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                  {metrics.changeFailureRate}%
                </div>
              </div>

              {/* MTTR */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-light-text dark:text-foreground">
                    Mean Time to Recovery
                  </span>
                  <span
                    className={`text-xs font-bold ${
                      metrics.mttr <= 1
                        ? 'text-green-500'
                        : metrics.mttr <= 24
                        ? 'text-blue-500'
                        : metrics.mttr <= 168
                        ? 'text-yellow-500'
                        : 'text-red-500'
                    }`}
                  >
                    {metrics.mttr <= 1
                      ? 'Elite'
                      : metrics.mttr <= 24
                      ? 'High'
                      : metrics.mttr <= 168
                      ? 'Medium'
                      : 'Low'}
                  </span>
                </div>
                <div className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                  {metrics.mttr} hours
                </div>
              </div>
            </div>
          </div>

          {/* Benchmarks */}
          <div className="bg-white dark:bg-surface p-6 rounded-lg border border-gray-200 dark:border-border">
            <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-4">
              Industry Benchmarks
            </h2>
            <div className="space-y-3 text-sm">
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded">
                <div className="font-bold text-green-500 mb-1">Elite Performance</div>
                <div className="text-xs text-light-text-secondary dark:text-foreground-secondary space-y-1">
                  <div>• Deploy: Multiple per day</div>
                  <div>• Lead Time: &lt; 1 hour</div>
                  <div>• Failure Rate: 0-15%</div>
                  <div>• MTTR: &lt; 1 hour</div>
                </div>
              </div>

              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                <div className="font-bold text-blue-500 mb-1">High Performance</div>
                <div className="text-xs text-light-text-secondary dark:text-foreground-secondary space-y-1">
                  <div>• Deploy: Weekly to monthly</div>
                  <div>• Lead Time: &lt; 1 day</div>
                  <div>• Failure Rate: 0-15%</div>
                  <div>• MTTR: &lt; 1 day</div>
                </div>
              </div>
            </div>
          </div>

          {/* Custom Metrics Results */}
          {customMetrics.some((m) => m.name && m.value) && (
            <div className="bg-white dark:bg-surface p-6 rounded-lg border border-gray-200 dark:border-border">
              <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-4">
                Custom Metrics Status
              </h2>
              <div className="space-y-3">
                {customMetrics
                  .filter((m) => m.name && m.value)
                  .map((metric, index) => {
                    const current = parseFloat(metric.value) || 0;
                    const target = parseFloat(metric.target) || 0;
                    const isOnTrack = target > 0 && current <= target;

                    return (
                      <div
                        key={index}
                        className="p-3 bg-gray-50 dark:bg-surfaceer rounded border border-gray-200 dark:border-border-muted"
                      >
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-semibold text-light-text dark:text-foreground text-sm">
                            {metric.name}
                          </span>
                          {target > 0 && (
                            <span
                              className={`text-xs font-bold ${
                                isOnTrack ? 'text-green-500' : 'text-accent'
                              }`}
                            >
                              {isOnTrack ? '✓ On Track' : '⚠ Review'}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-light-text-secondary dark:text-foreground-secondary">
                          Current: {metric.value} {metric.unit}
                          {target > 0 && ` | Target: ${metric.target} ${metric.unit}`}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Export Button */}
          <button
            onClick={exportToExcel}
            className="w-full bg-accent text-accent-foreground px-6 py-3 rounded-lg font-semibold hover:bg-accent-hover transition-colors"
          >
            📊 Export to Excel
          </button>
        </div>
      </div>

      {/* How It Works */}
      <div className="mt-12 bg-white dark:bg-surface p-8 rounded-lg border border-gray-200 dark:border-border">
        <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-4">
          About DORA Metrics
        </h2>
        <div className="grid md:grid-cols-2 gap-6 text-light-text-secondary dark:text-foreground-secondary">
          <div>
            <h3 className="font-semibold text-light-text dark:text-foreground mb-2">
              What are DORA Metrics?
            </h3>
            <p className="text-sm">
              DORA (DevOps Research and Assessment) metrics are the industry-standard measures of
              software delivery performance. They predict business success and help teams improve.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-light-text dark:text-foreground mb-2">
              Why Track These?
            </h3>
            <p className="text-sm">
              Research shows that high-performing teams deploy more frequently, recover faster, and
              have fewer failures. Tracking DORA metrics helps identify bottlenecks and improvement
              opportunities.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-light-text dark:text-foreground mb-2">
              Deployment Frequency
            </h3>
            <p className="text-sm">
              How often code is deployed to production. Elite teams deploy multiple times per day,
              enabling faster feedback and reduced risk per deployment.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-light-text dark:text-foreground mb-2">
              Lead Time for Changes
            </h3>
            <p className="text-sm">
              Time from code commit to production. Shorter lead times enable faster iteration and
              quicker response to market changes and customer needs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
